/**
 * WhatsApp Service for sending receipt via WhatsApp Web
 * Fallback when thermal printer fails
 */

import type { ReceiptData } from './thermalPrinterService';

export class WhatsAppService {
  /**
   * Format receipt as text message
   */
  private formatReceiptText(data: ReceiptData): string {
    let text = '';

    // Header
    text += `*${data.branchName}*\n`;
    text += `Sparepart & Accessories\n`;
    text += `================================\n\n`;

    // Transaction info
    text += `📋 *${data.invoiceNumber}*\n`;
    text += `📅 ${data.date}\n`;
    text += `👤 Kasir: ${data.cashier}\n\n`;

    // Customer info
    if (data.customer) {
      text += `🧑‍💼 Customer: *${data.customer.name}*\n`;
      text += `📞 ${data.customer.phone}\n`;
      text += `🏷️ Tier: ${data.customer.tier}\n\n`;
    } else {
      text += `🧑‍💼 Customer: Walk-in (Retail +35%)\n\n`;
    }

    text += `================================\n\n`;

    // Items
    text += `*DETAIL PEMBELIAN*\n\n`;
    for (const item of data.items) {
      text += `${item.name}\n`;
      text += `${item.quantity} x Rp ${item.price.toLocaleString('id-ID')} = *Rp ${item.subtotal.toLocaleString('id-ID')}*\n\n`;
    }

    text += `================================\n\n`;

    // Totals
    text += `Subtotal: Rp ${data.subtotal.toLocaleString('id-ID')}\n`;

    if (data.markup && data.markup > 0) {
      const markupPct = ((data.markup / data.subtotal) * 100).toFixed(0);
      text += `Markup (+${markupPct}%): Rp ${data.markup.toLocaleString('id-ID')}\n`;
    }

    if (data.discount && data.discount > 0) {
      const discountPct = ((data.discount / data.subtotal) * 100).toFixed(0);
      text += `Diskon (-${discountPct}%): -Rp ${data.discount.toLocaleString('id-ID')}\n`;
    }

    text += `\n*TOTAL: Rp ${data.total.toLocaleString('id-ID')}*\n\n`;
    text += `================================\n\n`;

    // Payment info
    const paymentLabels: Record<string, string> = {
      cash: '💵 TUNAI',
      tempo: '📅 TEMPO',
      qris: '📱 QRIS',
      transfer: '🏦 TRANSFER',
    };
    text += `${paymentLabels[data.paymentMethod] || data.paymentMethod.toUpperCase()}\n`;

    if (data.paymentMethod === 'cash' && data.paid) {
      text += `Bayar: Rp ${data.paid.toLocaleString('id-ID')}\n`;
      text += `Kembali: Rp ${(data.change || 0).toLocaleString('id-ID')}\n`;
    } else if (data.paymentMethod === 'tempo' && data.dueDate) {
      text += `⏰ Jatuh Tempo: ${data.dueDate}\n`;
      text += `⚠️ *BELUM LUNAS*\n`;
    }

    text += `\n================================\n\n`;

    // Footer
    text += `✅ Terima kasih atas pembelian Anda!\n`;
    text += `_Barang yang sudah dibeli tidak dapat ditukar/dikembalikan_\n`;

    return text;
  }

  /**
   * Send receipt via WhatsApp Web
   * Opens WhatsApp Web with pre-filled message
   */
  sendReceipt(phoneNumber: string, receiptData: ReceiptData): void {
    // Format phone number (remove non-digits, add country code if needed)
    let phone = phoneNumber.replace(/\D/g, '');
    
    // Add Indonesia country code if not present
    if (!phone.startsWith('62')) {
      if (phone.startsWith('0')) {
        phone = '62' + phone.substring(1);
      } else {
        phone = '62' + phone;
      }
    }

    // Format receipt text
    const message = this.formatReceiptText(receiptData);

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);

    // Open WhatsApp Web with message
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  }

  /**
   * Send to multiple numbers (for group notification)
   */
  sendToMultiple(phoneNumbers: string[], receiptData: ReceiptData): void {
    phoneNumbers.forEach((phone) => {
      setTimeout(() => {
        this.sendReceipt(phone, receiptData);
      }, 500); // Small delay to avoid browser blocking
    });
  }

  /**
   * Validate phone number
   */
  isValidPhoneNumber(phone: string): boolean {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }
}

// Singleton instance
export const whatsappService = new WhatsAppService();
